import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { DeclarationView } from '@miraki/contracts';
import { api } from '../api/client';
import { useSession } from '../auth/session';
import { Alert, Empty, Money, Note, PageHeader, Panel, Spinner, Table, Tiles } from '../ui';

/**
 * A-01 Dashboard — DB-08 (Admin / Sales), DB-09 (Accounts), DB-10 (Manufacturing), DB-11 (Super
 * Admin), composed by the SERVER to the signed-in user's permissions (`dashboard/tiles.ts`).
 * There is one A-01, not four: a user who holds both sales and production permissions sees both
 * sets, because roles are runtime data (RL-05) and "the Accounts dashboard" is whatever an
 * accounts role is granted today.
 *
 * Every figure links to the screen that owns it (DB-12). Figures DB-08…DB-11 ask for whose
 * screen does not exist yet are not tiles: the API lists them, and this page says what each one
 * waits for, so a missing figure reads as missing rather than as zero.
 *
 * Portal-scoped (DB-13): the header names the portal and currency. Money is a list per currency
 * and is never added up here; a cross-currency total arrives REFUSED, with D72's reason.
 */

interface Amount { currency: string; amount: string }
type Pending<T> = { state: 'READY'; value: T } | { state: 'NOT_YET'; waitingFor: string };
type Consolidated = { kind: 'NONE' } | { kind: 'TOTAL'; currency: string; amount: string } | { kind: 'REFUSED'; reason: string };
interface Named { id: string; name: string }
type TileData = {
  applications: { count: number; oldestDays: number | null };
  ordersAwaiting: { count: number };
  /** DB-08 — the oldest request's timestamp, so "waiting since" is available if wanted. */
  returnsAwaiting: { count: number; oldest: string | null };
  nearBudget: Pending<{ count: number; retailers: Named[] }>;
  dormantRetailers: { afterDays: number; count: number; retailers: Array<Named & { days: number; ordered: boolean }> };
  renewalsDue: { count: number; expired: number; retailers: Array<Named & { renewsOn: string | null }> };
  /**
   * SL-04a — accounts holding pieces with no sale reported this period, with the month's deadline.
   *
   * The contract's own type, not a copy: the server composes against it, so a field it forgets to
   * send is a compile error rather than a blank cell. Null only defensively — the server does not
   * compose this tile at all on a portal with no month to close.
   */
  declarationsDue: DeclarationView | null;
  payablesOutstanding: { amounts: Amount[]; consolidated: Consolidated };
  payablesOverdue: { amounts: Amount[] };
  invoicesAgeing: { open: Amount[]; ageing: Array<{ band: string; invoices: number; open: Amount[] }> };
  creditsHeld: { retailers: number; amounts: Amount[] };
  awaitingInvoice: Pending<{ pieces: number; retailers: number; amounts: Amount[]; oldestSoldOn: string | null }>;
  production: { byState: Array<{ state: string; label: string; orders: number }> };
  madeVsOrdered: { made: number; ordered: number };
  pastPromised: { count: number };
  madeNotShipped: { count: number };
  superAdmins: { count: number };
  accessChanges: { recent: Array<{ at: string; actorEmail: string | null; action: string; objectLabel: string | null }> };
};
type Tile = { [K in keyof TileData]: { id: K; requirement: string; drill: { page: string; route: string }; data: TileData[K] } }[keyof TileData];
interface Dashboard {
  portal: { code: string; name: string; currency: string };
  today: string;
  tiles: Tile[];
  notShown: Array<{ what: string; requirement: string; waitingFor: string }>;
}

const pick = <K extends keyof TileData>(tiles: Tile[], id: K) =>
  tiles.find((t) => t.id === id) as Extract<Tile, { id: K }> | undefined;

function Amounts({ list }: { list: readonly Amount[] }) {
  const { t } = useTranslation();
  if (list.length === 0) return <span className="mk-dim">{t('dashboard.none')}</span>;
  return <>{list.map((a) => <div key={a.currency}><Money amount={a.amount} currency={a.currency} /></div>)}</>;
}

export function Dashboard() {
  const { t } = useTranslation();
  const { portal } = useSession();
  const { data, isLoading, error } = useQuery({
    queryKey: ['portal', portal?.code ?? null, 'dashboard'],
    queryFn: () => api.get<Dashboard>('/dashboard'),
  });
  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  if (!data) return null;

  const tile = <K extends keyof TileData>(id: K) => pick(data.tiles, id);
  const go = (x: Tile) => <Link to={x.drill.route}>{t('dashboard.open')}</Link>;
  const items: Array<{ k: string; v: ReactNode; s?: ReactNode }> = [];

  // DB-08 — Admin / Sales
  const apps = tile('applications');
  if (apps) items.push({ k: t('dashboard.applications'), v: apps.data.count,
    s: <>{apps.data.oldestDays !== null ? t('dashboard.oldest', { n: apps.data.oldestDays }) : t('dashboard.queueClear')} · {go(apps)}</> });
  const awaiting = tile('ordersAwaiting');
  if (awaiting) items.push({ k: t('dashboard.ordersAwaiting'), v: awaiting.data.count, s: go(awaiting) });
  const near = tile('nearBudget');
  if (near) items.push({ k: t('dashboard.nearBudget'),
    v: near.data.state === 'READY' ? near.data.value.count : '—',
    s: near.data.state === 'READY' ? go(near) : t('dashboard.notYet') });
  const dormant = tile('dormantRetailers');
  if (dormant) items.push({ k: t('dashboard.dormant', { n: dormant.data.afterDays }), v: dormant.data.count, s: go(dormant) });
  // DB-08 — return requests waiting on a decision, drilling to A-43.
  const returns = tile('returnsAwaiting');
  if (returns) items.push({ k: t('dashboard.returnsAwaiting'), v: returns.data.count, s: go(returns) });
  const renewals = tile('renewalsDue');
  if (renewals) items.push({ k: t('dashboard.renewals'), v: renewals.data.count,
    s: <>{t('dashboard.expired', { n: renewals.data.expired })} · {go(renewals)}</> });
  /*
   * SL-04a — the chase list. A COUNT AND A DATE, no amount of money: declaration is not a money
   * state, which is also why it sits here among DB-08's queues and not beside DB-09's figures.
   *
   * The server only composes it from the last day of the month through day 7, so there is nothing
   * here to hide the rest of the month: the tile's absence IS the rule (DB-07).
   */
  const declarations = tile('declarationsDue');
  if (declarations?.data) {
    const d = declarations.data;
    items.push({ k: t('declarations.tile'), v: d.count,
      s: <>{d.state === 'LATE'
        ? t('declarations.tileLate', { deadline: d.deadline })
        : t('declarations.tileDue', { deadline: d.deadline })} · {go(declarations)}</> });
  }

  // DB-09 — Accounts
  const outstanding = tile('payablesOutstanding');
  if (outstanding) items.push({ k: t('dashboard.outstanding'), v: <Amounts list={outstanding.data.amounts} />,
    s: <>{outstanding.data.consolidated.kind === 'REFUSED' ? t('dashboard.noCombined') : null} {go(outstanding)}</> });
  const overdue = tile('payablesOverdue');
  if (overdue) items.push({ k: t('dashboard.overdue'), v: <Amounts list={overdue.data.amounts} />, s: go(overdue) });
  const credits = tile('creditsHeld');
  if (credits) items.push({ k: t('dashboard.credits'), v: <Amounts list={credits.data.amounts} />,
    s: <>{t('dashboard.creditRetailers', { n: credits.data.retailers })} · {go(credits)}</> });
  // A count of pieces, not A-42's "sold, not yet invoiced" money: see `awaitingInvoice()` on the server.
  const toInvoice = tile('awaitingInvoice');
  if (toInvoice) {
    const v = toInvoice.data.state === 'READY' ? toInvoice.data.value : null;
    items.push({ k: t('dashboard.awaitingInvoice'), v: v ? v.pieces : '—',
      s: v ? <>{t('dashboard.awaitingPieces', { n: v.pieces, r: v.retailers })}
        {v.oldestSoldOn ? <> · {t('dashboard.awaitingSince', { day: v.oldestSoldOn })}</> : null}
        {v.amounts.length ? <Amounts list={v.amounts} /> : null} {go(toInvoice)}</> : t('dashboard.notYet') });
  }

  // DB-10 — Manufacturing
  const made = tile('madeVsOrdered');
  if (made) items.push({ k: t('dashboard.madeVsOrdered'), v: `${made.data.made} / ${made.data.ordered}`, s: go(made) });
  const late = tile('pastPromised');
  if (late) items.push({ k: t('dashboard.pastPromised'), v: late.data.count, s: go(late) });
  const waiting = tile('madeNotShipped');
  if (waiting) items.push({ k: t('dashboard.madeNotShipped'), v: waiting.data.count, s: go(waiting) });

  // DB-11 — Super Admin
  const supers = tile('superAdmins');
  if (supers) items.push({ k: t('dashboard.superAdmins'), v: supers.data.count, s: go(supers) });

  const ageing = tile('invoicesAgeing');
  const production = tile('production');
  const changes = tile('accessChanges');

  return (
    <>
      <PageHeader title={t('dashboard.title')} sub={t('dashboard.sub', { portal: data.portal.name, currency: data.portal.currency, today: data.today })} />

      {items.length ? <Tiles items={items} /> : (
        <Empty title={t('dashboard.emptyTitle')} body={t('dashboard.emptyBody')} />
      )}

      <div className="mk-grid2">
        {production ? (
          <Panel title={t('dashboard.production')} actions={go(production)}>
            <Table rows={production.data.byState} rowKey={(r) => r.state}
              columns={[
                { header: t('dashboard.stage'), render: (r) => r.label },
                { header: t('dashboard.orders'), numeric: true, render: (r) => r.orders },
              ]} />
          </Panel>
        ) : null}

        {ageing ? (
          <Panel title={t('dashboard.ageing')} actions={go(ageing)}>
            <Table rows={ageing.data.ageing} rowKey={(r) => r.band}
              columns={[
                { header: t('dashboard.days'), render: (r) => r.band },
                { header: t('dashboard.invoices'), numeric: true, render: (r) => r.invoices },
                { header: t('dashboard.openAmount'), numeric: true, render: (r) => <Amounts list={r.open} /> },
              ]} />
          </Panel>
        ) : null}

        {dormant && dormant.data.retailers.length ? (
          <Panel title={t('dashboard.dormant', { n: dormant.data.afterDays })}>
            <Table rows={dormant.data.retailers} rowKey={(r) => r.id}
              columns={[
                { header: t('dashboard.retailer'), render: (r) => <Link to={`/retailers/${r.id}`}>{r.name}</Link> },
                { header: t('dashboard.days'), numeric: true,
                  render: (r) => <>{r.days}{r.ordered ? null : <span className="mk-hint"> {t('dashboard.neverOrdered')}</span>}</> },
              ]} />
          </Panel>
        ) : null}

        {/*
          * SL-04a — who to call, named on A-01 rather than only counted.
          *
          * The tile drills to A-40, whose page floor is `finance.view`; this tile is gated on
          * `retailer.view`, which the seeded Sales role holds and `finance.view` it does not. So
          * the five names are here, where the chasing happens, and the full list is behind the
          * drill for Accounts. It says what the list IS, because the portal cannot tell a quiet
          * month from a silent one — never "non-compliant".
          */}
        {declarations?.data && declarations.data.retailers.length ? (
          <Panel title={t('declarations.title')} actions={go(declarations)}>
            <div className="mk-pbody">
              {/* The count, because the table below it shows at most five of them. */}
              <Note>{t('declarations.tileHint', { count: declarations.data.count })}</Note>
              <Note>{t('declarations.conversation')}</Note>
            </div>
            <Table rows={declarations.data.retailers} rowKey={(r) => r.retailerId}
              columns={[
                { header: t('declarations.colRetailer'),
                  render: (r) => <Link to={`/retailers/${r.retailerId}`}>{r.retailerName}</Link> },
                { header: t('declarations.colPieces'), numeric: true, render: (r) => r.piecesHeld },
                { header: t('declarations.colLastReported'),
                  render: (r) => <span className="mk-dim">{r.lastReportedOn ?? t('declarations.never')}</span> },
              ]} />
          </Panel>
        ) : null}

        {changes ? (
          <Panel title={t('dashboard.accessChanges')} actions={go(changes)}>
            <Table rows={changes.data.recent} rowKey={(r) => `${r.at}-${r.action}`}
              columns={[
                { header: t('dashboard.when'), render: (r) => <span className="mk-dim">{r.at.slice(0, 16)}</span> },
                { header: t('dashboard.what'), render: (r) => <>{r.action} <span className="mk-dim">{r.objectLabel ?? ''}</span></> },
                { header: t('dashboard.who'), render: (r) => <span className="mk-dim">{r.actorEmail ?? '—'}</span> },
              ]}
              empty={<Empty title={t('dashboard.noChanges')} />} />
          </Panel>
        ) : null}
      </div>

      {data.notShown.length ? (
        <Panel title={t('dashboard.notShownTitle')} pad>
          <Note>{t('dashboard.notShownBody')}</Note>
          <ul>
            {data.notShown.map((n) => <li key={n.what}><b>{n.requirement}</b> {n.what} — <span className="mk-dim">{n.waitingFor}</span></li>)}
          </ul>
        </Panel>
      ) : null}
    </>
  );
}
